import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import FontFamily from '@tiptap/extension-font-family';
import FontSize from '@tiptap/extension-font-size';
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  Mail,
  Send,
  FileStack,
  X,
  Inbox,
  List,
  ListOrdered,
  Link2,
  Eraser,
} from 'lucide-react';
import { showPmrSuccess } from '../../../utils/swalUtils';
import './emailSent.css';

const showEmailSentSuccessPopup = () =>
  showPmrSuccess('Your email was sent successfully.');

/** Split To/Cc/Bcc strings on commas, semicolons, or newlines. */
const splitRecipientTokens = (value) =>
  String(value || '')
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);

/** Use address inside angle brackets (Name <email>) when present, otherwise the whole token. */
const parseRecipientAddress = (token) => {
  const t = String(token || '').trim();
  if (!t) return '';
  const bracket = t.match(/<([^>]+)>\s*$/);
  return (bracket ? bracket[1] : t).trim();
};

/**
 * Practical single-address check (ASCII-oriented; matches typical corporate addresses).
 */
const isValidEmailAddress = (addr) => {
  const a = String(addr || '').trim();
  if (!a || a.length > 254) return false;
  return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(a);
};

const validateRecipientList = (value, { allowEmpty }) => {
  const tokens = splitRecipientTokens(value);
  if (tokens.length === 0) {
    return { ok: allowEmpty, tokens: [] };
  }
  const invalid = tokens.filter((t) => !isValidEmailAddress(parseRecipientAddress(t)));
  return { ok: invalid.length === 0, tokens, invalid };
};

const getDefaultMultipleEmailSubject = () => {
  const generatedOn = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `Your Selected Reports – Generated on ${generatedOn}`;
};

const MultipleEmailDialog = ({
  show,
  onHide,
  onSave,
  report_id,
  token,
  reportName,
  isSharedReport,
  selectedReports,
  user_id,
  from_email = '',
}) => {
  const [messageBody, setMessageBody] = useState('');
  const [toRecipients, setToRecipients] = useState('');
  const [ccRecipients, setCcRecipients] = useState('');
  const [bccRecipients, setBccRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [attachedReports, setAttachedReports] = useState([]);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [attemptedSend, setAttemptedSend] = useState(false);
  const [touched, setTouched] = useState({ to: false, cc: false, bcc: false });

  const toCount = useMemo(() => splitRecipientTokens(toRecipients).length, [toRecipients]);
  const ccCount = useMemo(() => splitRecipientTokens(ccRecipients).length, [ccRecipients]);
  const bccCount = useMemo(() => splitRecipientTokens(bccRecipients).length, [bccRecipients]);
  const displayReports = useMemo(() => {
    const raw = Array.isArray(selectedReports) ? selectedReports : [];
    const normalized = raw
      .filter((r) => r != null)
      .map((r) => ({
        reportId: r.reportId ?? r.report_id,
        name: (r.name || r.report_name || 'Report').trim() || 'Report',
        date: r.date ?? r.report_date,
        category: r.category ?? r.group_name,
        clinic: r.clinic ?? r.hospital_name,
      }))
      .filter((r) => r.reportId != null && r.reportId !== '');

    if (normalized.length > 0) {
      return normalized;
    }
    if (report_id != null && report_id !== '') {
      return [{ reportId: report_id, name: (reportName && String(reportName).trim()) || 'Report' }];
    }
    return [];
  }, [selectedReports, report_id, reportName]);

  const handleEditorUpdate = useCallback(({ editor }) => {
    setMessageBody(editor.getHTML());
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Strike,
      Code,
      CodeBlock,
      Subscript,
      Superscript,
      TextStyle,
      Color,
      Highlight,
      Link,
      Placeholder.configure({ placeholder: 'Write your message…' }),
      FontFamily,
      FontSize.configure({
        types: ['textStyle'],
      }),
    ],
    content: messageBody,
    onUpdate: handleEditorUpdate,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none email-compose-prose',
      },
    },
  });

  const Toolbar = () => {
    if (!editor) return null;

    const handleFormattingClick = (formatType) => {
      switch (formatType) {
        case 'bold':
          editor.chain().focus().toggleBold().run();
          break;
        case 'italic':
          editor.chain().focus().toggleItalic().run();
          break;
        case 'underline':
          editor.chain().focus().toggleUnderline().run();
          break;
        case 'code':
          editor.chain().focus().toggleCode().run();
          break;
        default:
          break;
      }
    };

    const handleAlignmentClick = (alignment) => {
      editor.chain().focus().setTextAlign(alignment).run();
    };

    return (
      <div className="tiptap-toolbar exact-toolbar email-compose-toolbar" style={{ overflow: 'visible' }}>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleFormattingClick('bold')}
          className={`toolbar-button ${editor.isActive('bold') ? 'is-active' : ''}`}
          title="Bold"
        >
          <span style={{ fontWeight: 'bold', fontSize: '16px' }}>B</span>
        </button>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleFormattingClick('italic')}
          className={`toolbar-button ${editor.isActive('italic') ? 'is-active' : ''}`}
          title="Italic"
        >
          <span style={{ fontStyle: 'italic', fontSize: '16px' }}>I</span>
        </button>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleFormattingClick('underline')}
          className={`toolbar-button ${editor.isActive('underline') ? 'is-active' : ''}`}
          title="Underline"
        >
          <span style={{ textDecoration: 'underline', fontSize: '16px' }}>U</span>
        </button>

        <div className="toolbar-divider" />

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleAlignmentClick('left')}
          className={`toolbar-button ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`}
          title="Align Left"
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleAlignmentClick('center')}
          className={`toolbar-button ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`}
          title="Align Center"
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleAlignmentClick('right')}
          className={`toolbar-button ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`}
          title="Align Right"
        >
          <AlignRight size={16} />
        </button>

        <div className="toolbar-divider" />

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleFormattingClick('code')}
          className={`toolbar-button ${editor.isActive('code') ? 'is-active' : ''}`}
          title="Inline code"
        >
          {'<>'}
        </button>

        <div className="toolbar-divider" />

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`toolbar-button ${editor.isActive('bulletList') ? 'is-active' : ''}`}
          title="Bulleted list"
        >
          <List size={16} />
        </button>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`toolbar-button ${editor.isActive('orderedList') ? 'is-active' : ''}`}
          title="Numbered list"
        >
          <ListOrdered size={16} />
        </button>

        <div className="toolbar-divider" />

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const previousUrl = editor.getAttributes('link').href;
            const url = window.prompt('Link URL', previousUrl || 'https://');
            if (url === null) return;
            if (url === '') {
              editor.chain().focus().extendMarkRange('link').unsetLink().run();
              return;
            }
            const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
            editor.chain().focus().extendMarkRange('link').setLink({ href, target: '_blank', rel: 'noopener noreferrer' }).run();
          }}
          className={`toolbar-button ${editor.isActive('link') ? 'is-active' : ''}`}
          title="Insert / edit link"
        >
          <Link2 size={16} />
        </button>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          className="toolbar-button"
          title="Clear formatting"
        >
          <Eraser size={16} />
        </button>
      </div>
    );
  };

  const resetDraft = useCallback(() => {
    setToRecipients('');
    setCcRecipients('');
    setBccRecipients('');
    setSubject(getDefaultMultipleEmailSubject());
    setMessageBody('');
    setShowCcBcc(false);
    setAttemptedSend(false);
    setTouched({ to: false, cc: false, bcc: false });
    setAttachedReports(displayReports.map((r) => ({ ...r })));
    if (editor && !editor.isDestroyed) {
      editor.chain().focus().clearContent().run();
    }
  }, [editor, displayReports]);

  const prevShowRef = useRef(false);
  useEffect(() => {
    const justOpened = show && !prevShowRef.current;
    prevShowRef.current = show;
    if (!justOpened) return;

    setToRecipients('');
    setCcRecipients('');
    setBccRecipients('');
    setSubject(getDefaultMultipleEmailSubject());
    setMessageBody('');
    setShowCcBcc(false);
    setAttemptedSend(false);
    setTouched({ to: false, cc: false, bcc: false });

    const clearEditor = () => {
      if (editor && !editor.isDestroyed) {
        editor.chain().clearContent().run();
      }
    };
    clearEditor();
    const id = requestAnimationFrame(clearEditor);
    return () => cancelAnimationFrame(id);
  }, [show, editor]);

  useLayoutEffect(() => {
    if (!show) return;
    setAttachedReports(displayReports.map((r) => ({ ...r })));
  }, [show, displayReports]);

  const recipientValidation = useMemo(() => {
    const to = validateRecipientList(toRecipients, { allowEmpty: false });
    const cc = validateRecipientList(ccRecipients, { allowEmpty: true });
    const bcc = validateRecipientList(bccRecipients, { allowEmpty: true });
    const ccTrim = ccRecipients.trim();
    const bccTrim = bccRecipients.trim();
    return {
      toOk: to.ok && to.tokens.length > 0,
      ccOk: !ccTrim || (cc.ok && cc.tokens.length > 0),
      bccOk: !bccTrim || (bcc.ok && bcc.tokens.length > 0),
      to,
      cc,
      bcc,
    };
  }, [toRecipients, ccRecipients, bccRecipients]);

  const hasReportIds = useMemo(
    () =>
      attachedReports.some((r) => r.reportId != null && String(r.reportId).trim() !== ''),
    [attachedReports]
  );

  const sendButtonTitle = (() => {
    if (!hasReportIds) return 'Attach at least one report to send';
    if (!recipientValidation.toOk) {
      return toRecipients.trim()
        ? 'Enter at least one valid email in To (separate multiple with commas)'
        : 'Add at least one recipient in To';
    }
    if (!recipientValidation.ccOk) return 'Fix invalid email address(es) in Cc';
    if (!recipientValidation.bccOk) return 'Fix invalid email address(es) in Bcc';
    if (!onSave) return 'Provide an onSave handler to enable sending';
    return undefined;
  })();

  const handleSend = () => {
    if (!onSave || !hasReportIds) return;

    const recipientsValid =
      recipientValidation.toOk && recipientValidation.ccOk && recipientValidation.bccOk;
    if (!recipientsValid) {
      setAttemptedSend(true);
      if (
        (ccRecipients.trim() && !recipientValidation.ccOk) ||
        (bccRecipients.trim() && !recipientValidation.bccOk)
      ) {
        setShowCcBcc(true);
      }
      const firstInvalidId = !recipientValidation.toOk
        ? 'email-to'
        : !recipientValidation.ccOk
          ? 'email-cc'
          : 'email-bcc';
      const el = document.getElementById(firstInvalidId);
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
      return;
    }
    setAttemptedSend(false);

    const idList = attachedReports
      .map((r) => r.reportId)
      .filter((id) => id != null && String(id).trim() !== '');
    const report_ids = idList.join(',');
    const numericUserId = Number(user_id);
    const payload = {
      report_ids,
      user_id: Number.isFinite(numericUserId) ? numericUserId : user_id,
      to_email: toRecipients.trim(),
      from_email: String(from_email ?? '').trim(),
      cc_email: ccRecipients.trim(),
      bcc_email: bccRecipients.trim(),
      email_subject: subject.trim(),
      email_body: messageBody,
      token,
    };

    try {
      onSave(payload);
    } catch (error) {
      console.error('Error sending email:', error);
      return;
    }

    resetDraft();
    onHide();
    showEmailSentSuccessPopup();
  };

  const handleSendRef = useRef(handleSend);
  handleSendRef.current = handleSend;

  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onHide && onHide();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (handleSendRef.current) handleSendRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [show, onHide]);

  const messageWordCount = useMemo(() => {
    if (!editor || editor.isDestroyed) return 0;
    const text = editor.getText().trim();
    if (!text) return 0;
    return text.split(/\s+/).length;
  }, [editor, messageBody]);

  if (!show) return null;

  return (
    <div className="modal-overlay email-compose-overlay">
      <div className="modal-container email-compose-modal" role="dialog" aria-labelledby="email-compose-title">
        <header className="email-compose-header">
          <div className="email-compose-header-main">
            <div className="email-compose-header-icon" aria-hidden>
              <Mail size={22} strokeWidth={2} />
            </div>
            <div className="email-compose-header-text">
              <h2 id="email-compose-title" className="email-compose-title">Compose email</h2>
              {displayReports.length > 0 ? (
                <p className="email-compose-subtitle">
                  <span className="email-compose-subtitle-label">Including</span>
                  {attachedReports.length === 0 ? (
                    <span className="email-compose-subtitle--warn">No reports attached</span>
                  ) : attachedReports.length === 1 ? (
                    attachedReports[0].name
                  ) : (
                    `${attachedReports.length} selected reports`
                  )}
                </p>
              ) : (
                <p className="email-compose-subtitle email-compose-subtitle--muted">
                  Add recipients and your message below
                </p>
              )}
            </div>
          </div>
          <button type="button" className="email-compose-close" onClick={onHide} aria-label="Close dialog">
            ×
          </button>
        </header>

        <div className="modal-body-add-note email-compose-body">
          <div className="email-compose-scroll email-compose-scroll--split">
            <div className="email-compose-column email-compose-column--left">
            <section className="email-compose-card" aria-label="Recipients">
              <div className="email-recipient-fields email-recipient-fields--flush">
                <div className="email-recipient-row">
                  <label className="email-recipient-label" htmlFor="email-to">
                    To
                    <span className="email-compose-required-mark" title="Required" aria-label="required">*</span>
                  </label>
                  <div className="email-recipient-input-wrap">
                    <input
                      id="email-to"
                      type="text"
                      className={`email-recipient-input${!recipientValidation.toOk && (touched.to || attemptedSend) ? ' email-recipient-input--invalid' : ''}`}
                      placeholder="name@example.com, …"
                      value={toRecipients}
                      onChange={(e) => setToRecipients(e.target.value)}
                      onBlur={() => setTouched((t) => ({ ...t, to: true }))}
                      autoComplete="email"
                      required
                      aria-required="true"
                      aria-invalid={!recipientValidation.toOk && (touched.to || attemptedSend)}
                    />
                    {toCount > 1 && (
                      <span className="email-recipient-count" aria-label={`${toCount} recipients`}>
                        {toCount}
                      </span>
                    )}
                  </div>
                  {!showCcBcc && (
                    <button
                      type="button"
                      className="email-recipient-toggle"
                      onClick={() => setShowCcBcc(true)}
                      aria-expanded={showCcBcc}
                      aria-controls="email-cc-bcc-wrap"
                      title="Add Cc and Bcc recipients"
                    >
                      Cc / Bcc
                    </button>
                  )}
                </div>
                {showCcBcc && (
                  <div id="email-cc-bcc-wrap" className="email-recipient-extra">
                    <div className="email-recipient-row">
                      <label className="email-recipient-label" htmlFor="email-cc">Cc</label>
                      <div className="email-recipient-input-wrap">
                        <input
                          id="email-cc"
                          type="text"
                          className={`email-recipient-input${!recipientValidation.ccOk && (touched.cc || attemptedSend) ? ' email-recipient-input--invalid' : ''}`}
                          placeholder="Optional"
                          value={ccRecipients}
                          onChange={(e) => setCcRecipients(e.target.value)}
                          onBlur={() => setTouched((t) => ({ ...t, cc: true }))}
                          autoComplete="off"
                          aria-invalid={!recipientValidation.ccOk && (touched.cc || attemptedSend)}
                        />
                        {ccCount > 1 && (
                          <span className="email-recipient-count" aria-label={`${ccCount} Cc recipients`}>
                            {ccCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="email-recipient-row">
                      <label className="email-recipient-label" htmlFor="email-bcc">Bcc</label>
                      <div className="email-recipient-input-wrap">
                        <input
                          id="email-bcc"
                          type="text"
                          className={`email-recipient-input${!recipientValidation.bccOk && (touched.bcc || attemptedSend) ? ' email-recipient-input--invalid' : ''}`}
                          placeholder="Optional"
                          value={bccRecipients}
                          onChange={(e) => setBccRecipients(e.target.value)}
                          onBlur={() => setTouched((t) => ({ ...t, bcc: true }))}
                          autoComplete="off"
                          aria-invalid={!recipientValidation.bccOk && (touched.bcc || attemptedSend)}
                        />
                        {bccCount > 1 && (
                          <span className="email-recipient-count" aria-label={`${bccCount} Bcc recipients`}>
                            {bccCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="email-recipient-row email-recipient-row--subject">
                  <label className="email-recipient-label" htmlFor="email-subject">Subject</label>
                  <div className="email-recipient-input-wrap">
                    <input
                      id="email-subject"
                      type="text"
                      className="email-recipient-input"
                      placeholder="Add a subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      maxLength={200}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
            </section>

            {displayReports.length > 0 && (
              <section
                className="email-compose-reports-section"
                aria-label="Reports included with this email"
              >
                <div className="email-compose-reports-head">
                  <div className="email-compose-reports-head-icon" aria-hidden>
                    <FileStack size={20} strokeWidth={2} />
                  </div>
                  <div className="email-compose-reports-head-copy">
                    <h3 className="email-compose-reports-heading">Attachments in this email</h3>
                    <p className="email-compose-reports-lede">
                      {attachedReports.length === 0
                        ? 'No attachments in this email'
                        : attachedReports.length === 1
                          ? '1 attachment shown below; remove if not needed.'
                          : `${attachedReports.length} attachments shown side by side below; remove any you do not want.`}
                    </p>
                  </div>
                  <span className="email-compose-reports-count-pill" aria-hidden>
                    {attachedReports.length}
                  </span>
                </div>
                {attachedReports.length === 0 ? (
                  <div className="email-compose-reports-empty-state" role="status">
                    <div className="email-compose-reports-empty-visual" aria-hidden>
                      <Inbox size={22} strokeWidth={1.75} />
                    </div>
                    <p className="email-compose-reports-empty-title">No reports in this email</p>
                    <p className="email-compose-reports-empty-hint">
                      Use <strong className="email-compose-reports-empty-kbd">Clear draft</strong> to restore the full list.
                    </p>
                  </div>
                ) : (
                  <div className="email-compose-reports-list-outer">
                    <ul className="email-compose-reports-list" role="list">
                      {attachedReports.map((report, index) => (
                          <li
                            key={`${String(report.reportId)}-${index}`}
                            className="email-compose-report-item"
                          >
                            <span className="email-compose-report-title" title={report.name}>
                              {report.name}
                            </span>
                            <button
                              type="button"
                              className="email-compose-report-remove"
                              onClick={() => setAttachedReports((prev) => prev.filter((_, i) => i !== index))}
                              aria-label={`Remove ${report.name} from this email`}
                              title="Remove from this email"
                            >
                              <X size={15} strokeWidth={2.25} aria-hidden />
                            </button>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </section>
            )}
            </div>

            <div className="email-compose-column email-compose-column--right">
            <section className="email-compose-editor-section" aria-label="Message">
              <div className="email-compose-editor-label-row">
                <span className="email-compose-section-label">Message</span>
                {messageWordCount > 0 && (
                  <span className="email-compose-section-hint" aria-live="polite">
                    {messageWordCount} {messageWordCount === 1 ? 'word' : 'words'}
                  </span>
                )}
              </div>
              <div className="email-compose-editor-shell">
                <div className="tiptap-editor email-compose-tiptap">
                  <Toolbar />
                  <div className="email-compose-editor-scroll">
                    <EditorContent editor={editor} />
                  </div>
                </div>
              </div>
            </section>
            </div>

            {/* {!onSave && (
              <p className="email-compose-config-hint" role="status">
                Pass an <code className="email-compose-code">onSave</code> handler from the parent to send this message from your API.
              </p>
            )} */}
          </div>
        </div>

        <footer className="email-compose-footer">
          <div className="email-compose-footer-left">
            <button type="button" className="email-compose-btn email-compose-btn--secondary" onClick={onHide}>
              Cancel
            </button>
            <span className="email-compose-shortcut-hint" aria-hidden>
              <kbd className="email-compose-kbd">Ctrl</kbd>
              <span className="email-compose-kbd-plus">+</span>
              <kbd className="email-compose-kbd">Enter</kbd>
              <span className="email-compose-shortcut-text">to send</span>
            </span>
          </div>
          <div className="email-compose-footer-right">
            <button type="button" className="email-compose-btn email-compose-btn--ghost" onClick={resetDraft}>
              Clear draft
            </button>
            <button
              type="button"
              className="email-compose-btn email-compose-btn--primary"
              onClick={handleSend}
              disabled={!hasReportIds || !onSave}
              title={sendButtonTitle}
            >
              <Send size={18} aria-hidden />
              Send
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default MultipleEmailDialog;
